/* ========================================================================= *
 * TradeOrders — the trades on the worklist, as WORK ORDERS: an order and a
 * reason (the-work-order-pattern), never a list of rows to interpret.
 *
 * ⚠️ IT RECONCILES ON ITS FACE. Every trade on the desk lands in exactly one
 * of: work for the person being viewed, already settled, or somebody else's —
 * and the sentence under the heading says those three add up to the total,
 * beside trades = linked + flagged. A section that showed three orders and
 * said nothing about the other forty would be the invisible-record fault.
 *
 * ⚠️ A REFUSAL IS SAID IN WORDS. If the link index cannot be read this
 * section says so; it never renders as "no trades".
 * ========================================================================= */
import { Link } from 'react-router-dom'
import { useTradeLinks, refreshTradeLinks } from '../../lib/useTradeLinks.js'
import { ordersFor, RECONCILE_FAILED } from '../../lib/tradeLinks.js'
import styles from './Trades.module.css'

const nf = (n) => Number(n || 0).toLocaleString()

export default function TradeOrders({ who = '', isExecutive = false }) {
  const { status, links, error } = useTradeLinks()

  if (!links) {
    return (
      <section className={styles.section} data-tone={status === 'error' ? 'bad' : undefined}
        aria-label="Trades">
        <p className={styles.head}>Trades</p>
        <p className={styles.sentence}>
          {status === 'error'
            ? <>The trade links could not be read: {error} This is a refusal, not &ldquo;no
              trades&rdquo;.</>
            : 'Reading every trade on the desk and keying it to its records…'}
        </p>
        {status === 'error' ? (
          <div className={styles.controls}>
            <button type="button" className={styles.cta} data-primary="1"
              onClick={() => refreshTradeLinks({ force: true })}>Read the trades again</button>
          </div>
        ) : null}
      </section>
    )
  }

  const t = links.totals
  const o = ordersFor(links, { who, isExecutive: isExecutive || links.viewer?.isExecutive === true })
  const whose = !who || who === '*' ? 'the team' : who

  return (
    <section className={styles.section} data-tone={t.flagged || !t.reconciles ? 'bad' : undefined}
      aria-label="Trades">
      <p className={styles.head}>
        Trades — {nf(o.orders.length)} {o.orders.length === 1 ? 'order' : 'orders'} for {whose}
      </p>
      <p className={styles.recon}>
        <b>{nf(t.trades)}</b> {t.trades === 1 ? 'trade' : 'trades'} on the desk ={' '}
        <b>{nf(t.linked)}</b> linked + <b>{nf(t.flagged)}</b> flagged.{' '}
        For {whose}: <b>{nf(o.orders.length)}</b> to act on · <b>{nf(o.settled)}</b> already
        settled · <b>{nf(o.others)}</b> with somebody else.
        {links.read?.indexAgeMs != null
          ? ` Records keyed against a read ${Math.round(links.read.indexAgeMs / 1000)}s old.` : ''}
      </p>
      {!t.reconciles || !o.sums ? <p className={styles.fail} role="alert">{RECONCILE_FAILED}</p> : null}
      {links.read?.why ? <p className={styles.sentence}>⚠️ {links.read.why}</p> : null}

      {t.trades === 0 ? (
        <p className={styles.sentence}>
          No trades were returned by the current scoped reads of the desk and one-record proposals.
          The returned scope is empty; this is not proof that no trade has ever existed across
          the team. Open the Trade Desk to review what your current access permits.
        </p>
      ) : null}
      {t.trades === 0 ? (
        <div className={styles.controls}>
          <Link className={styles.cta} data-primary="1" to="/trade-desk">Open the Trade Desk</Link>
        </div>
      ) : null}

      {o.orders.length ? (
        <ul className={styles.list}>
          {o.orders.map((x) => (
            <li key={x.id} className={styles.order} data-flagged={x.flagged ? '1' : undefined}>
              <p className={styles.verb}>{x.verb}</p>
              <p className={styles.reason}>{x.reason}</p>
              <div className={styles.controls}>
                {x.ctas.map((c) => (c.retry ? (
                  <button key={c.label} type="button" className={styles.cta}
                    data-primary={c.primary ? '1' : undefined}
                    onClick={() => refreshTradeLinks({ force: true })}>{c.label}</button>
                ) : (
                  <Link key={c.label} className={styles.cta} data-primary={c.primary ? '1' : undefined}
                    to={c.to}>{c.label}</Link>
                )))}
              </div>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  )
}
